import React from 'react';
import {
  CheckCircle,
  Users,
  Shield,
  Clock,
  Award,
  MapPin,
  Wrench,
  Phone,
  Star,
  Zap,
  TrendingUp,
  Target,
  Heart
} from 'lucide-react';

const AboutUs = () => {
  const services = [
    {
      title: 'Basic Service',
      price: '₹499',
      features: ['Oil Change', 'Air Filter Check', 'Basic Inspection', 'Chain Lubrication'],
      icon: '🔧',
      popular: false
    },
    {
      title: 'Premium Service',
      price: '₹999',
      features: ['Complete Tune-up', 'Brake Service', 'Spark Plug Change', 'All Fluids Top-up'],
      icon: '⭐',
      popular: true
    },
    {
      title: 'Complete Care',
      price: '₹1499',
      features: ['Full Service', 'Engine Cleaning', 'Suspension Check', '6-Month Warranty'],
      icon: '💎',
      popular: false
    }
  ];

  const whyUs = [
    { icon: <Shield className="w-6 h-6" />, title: '100% Genuine Parts', desc: 'Only authentic spares used' },
    { icon: <Users className="w-6 h-6" />, title: 'Expert Mechanics', desc: 'Certified & experienced' },
    { icon: <Clock className="w-6 h-6" />, title: 'Quick Service', desc: 'Same-day delivery' },
    { icon: <Award className="w-6 h-6" />, title: 'Quality Assured', desc: 'Warranty on services' },
    { icon: <MapPin className="w-6 h-6" />, title: 'Free Pickup', desc: 'Doorstep convenience' },
    { icon: <Zap className="w-6 h-6" />, title: 'Transparent Pricing', desc: 'No hidden charges' }
  ];

  const testimonials = [
    { name: 'Rahul Sharma', role: 'Software Engineer', text: 'Best bike service in Pune! Quick, reliable, and affordable.', rating: 5 },
    { name: 'Priya Patil', role: 'Designer', text: 'Loved the doorstep service. Very professional team.', rating: 5 },
    { name: 'Amit Kumar', role: 'Entrepreneur', text: 'Transparent pricing and genuine parts. Highly recommend!', rating: 5 }
  ];

  return (
    <div className="min-h-screen bg-white">

      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 min-h-[560px]">

            {/* Left — dark automotive */}
            <div className="bg-[#111111] text-white p-8 sm:p-12 lg:p-16 flex items-center relative overflow-hidden">
              {/* Subtle grid overlay */}
              <div
                className="absolute inset-0 opacity-[0.04]"
                style={{
                  backgroundImage: `linear-gradient(#FF6B35 1px, transparent 1px), linear-gradient(90deg, #FF6B35 1px, transparent 1px)`,
                  backgroundSize: '40px 40px',
                }}
              />
              <div className="relative z-10">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#FF6B35]/10 border border-[#FF6B35]/20 rounded-full text-[#FF6B35] text-sm font-semibold mb-6">
                  <span className="w-1.5 h-1.5 bg-[#FF6B35] rounded-full animate-pulse" />
                  Pune's #1 Bike Service Platform
                </div>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black mb-6 leading-tight">
                  We Keep Your Bike<br />
                  <span className="text-[#FF6B35]">Running Smooth</span>
                </h1>
                <p className="text-lg text-gray-400 mb-8 leading-relaxed">
                  Professional bike servicing at your doorstep. Expert care, genuine parts, and unbeatable prices.
                </p>
                <button
                  onClick={() => window.location.href = '/'}
                  className="inline-flex items-center gap-2 px-8 py-4 bg-[#FF6B35] hover:bg-[#E85520] text-white rounded-xl font-bold transition-all shadow-lg shadow-[#FF6B35]/20 hover:-translate-y-0.5"
                >
                  Book Service Now
                </button>
              </div>
            </div>

            {/* Right — stats */}
            <div className="bg-gray-50 p-8 sm:p-12 flex items-center justify-center">
              <div className="text-center w-full max-w-sm">
                <div className="text-8xl mb-8">🏍️</div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-white p-4 rounded-2xl shadow-md border border-gray-100">
                    <div className="text-2xl font-black text-[#FF6B35]">5K+</div>
                    <div className="text-xs text-gray-500 font-medium mt-0.5">Customers</div>
                  </div>
                  <div className="bg-white p-4 rounded-2xl shadow-md border border-gray-100">
                    <div className="text-2xl font-black text-green-600">4.8★</div>
                    <div className="text-xs text-gray-500 font-medium mt-0.5">Rating</div>
                  </div>
                  <div className="bg-white p-4 rounded-2xl shadow-md border border-gray-100">
                    <div className="text-2xl font-black text-[#FF6B35]">25+</div>
                    <div className="text-xs text-gray-500 font-medium mt-0.5">Centers</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mission & Vision */}
      <div className="py-16 sm:py-24 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-3 gap-6">
            <div className="bg-[#FF6B35]/5 p-8 rounded-3xl border border-[#FF6B35]/15">
              <div className="w-14 h-14 bg-[#FF6B35] rounded-2xl flex items-center justify-center mb-6">
                <Target className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Our Mission</h3>
              <p className="text-gray-600 leading-relaxed">
                To make bike servicing hassle-free, transparent, and accessible. We're building trust through quality service, genuine parts, and fair pricing.
              </p>
            </div>

            <div className="bg-green-50 p-8 rounded-3xl border border-green-100">
              <div className="w-14 h-14 bg-green-600 rounded-2xl flex items-center justify-center mb-6">
                <TrendingUp className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Our Vision</h3>
              <p className="text-gray-600 leading-relaxed">
                To become India's most trusted bike service platform, expanding to every city and making professional care accessible to all riders.
              </p>
            </div>

            <div className="bg-gray-900 p-8 rounded-3xl border border-gray-800">
              <div className="w-14 h-14 bg-[#FF6B35] rounded-2xl flex items-center justify-center mb-6">
                <Heart className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Our Values</h3>
              <p className="text-gray-400 leading-relaxed">
                Transparency, Quality, Reliability, and Customer Satisfaction. We treat every bike as our own and every customer as family.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Why Choose Us */}
      <div className="py-16 sm:py-24 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#FF6B35]/10 border border-[#FF6B35]/20 rounded-full text-[#FF6B35] text-xs font-bold uppercase tracking-widest mb-4">
              Why Us
            </div>
            <h2 className="text-3xl sm:text-4xl font-black text-gray-900 mb-3">Why Choose OkMechanic?</h2>
            <p className="text-gray-500">Six reasons why we're Pune's favorite</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {whyUs.map((item, index) => (
              <div
                key={index}
                className="group bg-white p-7 rounded-2xl border border-gray-100 hover:border-[#FF6B35]/30 hover:shadow-xl hover:shadow-[#FF6B35]/5 transition-all duration-300 hover:-translate-y-1"
              >
                <div className="w-12 h-12 bg-[#FF6B35]/10 group-hover:bg-[#FF6B35] rounded-xl flex items-center justify-center mb-5 text-[#FF6B35] group-hover:text-white transition-all duration-300">
                  {item.icon}
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-1.5">{item.title}</h3>
                <p className="text-gray-500 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Testimonials */}
      <div className="py-16 sm:py-24 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-black text-gray-900 mb-3">What Customers Say</h2>
            <p className="text-gray-500">Real reviews from real riders</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((test, index) => (
              <div key={index} className="bg-gray-50 p-8 rounded-2xl border border-gray-100">
                <div className="flex mb-4">
                  {[...Array(test.rating)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-700 mb-6 leading-relaxed text-sm">"{test.text}"</p>
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-gradient-to-br from-[#FF6B35] to-[#E85520] rounded-full flex items-center justify-center text-white font-bold text-sm mr-3">
                    {test.name.charAt(0)}
                  </div>
                  <div>
                    <div className="font-bold text-gray-900 text-sm">{test.name}</div>
                    <div className="text-xs text-gray-500">{test.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Service Areas */}
      <div className="py-16 sm:py-24 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <MapPin className="w-12 h-12 text-[#FF6B35] mx-auto mb-4" />
            <h2 className="text-3xl sm:text-4xl font-black text-gray-900 mb-3">We Serve Across Pune</h2>
            <p className="text-gray-500">Free pickup & delivery in these areas</p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {[
              'Kothrud', 'Baner', 'Hinjewadi', 'Kharadi', 'Viman Nagar', 'Hadapsar',
              'Wakad', 'Aundh', 'Shivajinagar', 'Koregaon Park', 'Pimpri', 'Chinchwad'
            ].map((area, index) => (
              <div
                key={index}
                className="bg-white p-4 rounded-xl text-center font-semibold text-gray-700 hover:bg-[#FF6B35] hover:text-white transition-all cursor-pointer shadow-sm hover:shadow-lg border border-gray-100 hover:border-[#FF6B35] text-sm"
              >
                {area}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-[#111111] text-white py-16 sm:py-20 px-4 relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: `linear-gradient(#FF6B35 1px, transparent 1px), linear-gradient(90deg, #FF6B35 1px, transparent 1px)`,
            backgroundSize: '50px 50px',
          }}
        />
        <div className="relative max-w-4xl mx-auto text-center">
          <Wrench className="w-16 h-16 mx-auto mb-6 text-[#FF6B35]" />
          <h2 className="text-3xl sm:text-5xl font-black mb-6">
            Your Bike Deserves the Best
          </h2>
          <p className="text-xl text-gray-400 mb-10 leading-relaxed">
            Join 5000+ happy customers who trust OkMechanic for their bike servicing needs
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-10">
            <button
              onClick={() => window.location.href = '/'}
              className="px-10 py-4 bg-[#FF6B35] hover:bg-[#E85520] text-white rounded-xl font-bold text-lg transition-all shadow-lg shadow-[#FF6B35]/20 hover:-translate-y-0.5"
            >
              Book Service Now
            </button>
            <a
              href="tel:+919923887090"
              className="px-10 py-4 bg-white/5 text-white rounded-xl font-bold text-lg hover:bg-white/10 transition-all border border-white/10 inline-flex items-center justify-center gap-2"
            >
              <Phone className="w-5 h-5" />
              Call: +91 9923887090
            </a>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-gray-500">
            {['Free Doorstep Service', 'Genuine Parts Only', '6-Month Warranty'].map((item) => (
              <div key={item} className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-[#FF6B35]" />
                <span>{item}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutUs;
